// packages/ui-kit/src/primitives/Badge.tsx
// C3F v18 — reference: design/system/components/core/Badge.{jsx,prompt.md}
// Status label, always in JetBrains Mono, pill-shaped. The TONE carries the
// meaning, never the brand color: `accent` exists but is not for operative
// states (Spectrum is not semantic).
import { type ReactNode } from "react";

export type BadgeTone = "neutral" | "info" | "success" | "warning" | "danger" | "accent";
export type BadgeVariant = "soft" | "solid" | "outline";
export type BadgeSize = "xs" | "sm";

interface Props {
  tone?: BadgeTone;
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** 6px status dot before the text (DS `dot`). */
  dot?: boolean;
  iconLeft?: ReactNode;
  children: ReactNode;
  className?: string;
}

// Soft tints are OPAQUE and pair only with their own ink (build.md): the
// neutral background is --bg-subtle, never the --bg-hover/--bg-active veils —
// a veil is semi-transparent and would tie the text contrast to the host
// surface. The transparent border keeps soft/outline the same box size.
const softCls: Record<BadgeTone, string> = {
  neutral: "bg-subtle text-fg-default border-transparent",
  info: "bg-info-soft text-info border-transparent",
  success: "bg-success-soft text-success border-transparent",
  warning: "bg-warning-soft text-warning border-transparent",
  danger: "bg-danger-soft text-danger border-transparent",
  accent: "bg-accent-soft text-accent-text border-transparent",
};

const outlineCls: Record<BadgeTone, string> = {
  neutral: "border-border-default text-fg-default",
  info: "border-info-strong text-info",
  success: "border-success-strong text-success",
  warning: "border-warning-strong text-warning",
  danger: "border-danger-strong text-danger",
  accent: "border-accent-strong text-accent-text",
};

// Solid fills use --fg-inverse, NOT fixed white: it swaps by itself — light
// ink on the saturated Paper-theme states, Night ink on the bright Night ones.
const solidCls: Record<BadgeTone, string> = {
  neutral: "bg-fg-default text-fg-inverse border-transparent",
  info: "bg-info text-fg-inverse border-transparent",
  success: "bg-success text-fg-inverse border-transparent",
  warning: "bg-warning text-fg-inverse border-transparent",
  danger: "bg-danger text-fg-inverse border-transparent",
  accent: "bg-accent-text text-fg-inverse border-transparent",
};

// Dot color = the tone ink (on solid it rides currentColor). Neutral text is
// --fg-default but its dot is --fg-muted, per the DS lookup.
const dotCls: Record<BadgeTone, string> = {
  neutral: "bg-fg-muted",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  accent: "bg-accent-text",
};

// DS sizes: xs 20px / sm 24px tall. The DS reference types xs at 10px, but the
// v18 scale is closed (build.md: nothing outside 11/12/13/14/16/18/19/24) —
// xs sits on 11, sm on --fs-mono 12 (`text-mono`).
const sizeCls: Record<BadgeSize, string> = {
  xs: "h-5 px-[7px] text-[11px]",
  sm: "h-6 px-2.5 text-mono",
};

export function Badge({
  tone = "neutral",
  variant = "soft",
  size = "sm",
  dot = false,
  iconLeft,
  children,
  className = "",
}: Props) {
  const styleMap = { soft: softCls, outline: outlineCls, solid: solidCls };
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border font-mono font-medium tracking-mono ${styleMap[variant][tone]} ${sizeCls[size]} ${className}`}
    >
      {dot && (
        <span
          aria-hidden
          className={`h-1.5 w-1.5 rounded-full ${variant === "solid" ? "bg-current" : dotCls[tone]}`}
        />
      )}
      {iconLeft}
      {children}
    </span>
  );
}
